import { Box, ClickAwayListener, SxProps, Typography } from '@mui/material';
import React, { useState } from 'react';
import { TypeOf } from 'zod';
import {
  summaryWrapStyles,
  summaryErrorsStyles,
  summaryErrorStyles,
  summaryErrorTextStyles,
  summaryErrorIconStyles,
  summaryTopStyles,
} from './SummaryErrorsProps';
import ErrorIcon from '../../assets/icons/ErrorIcon';
import Chip from '../Chip/Chip';
import { extractRelatedClientName } from '@forwardslashns/taskit-validation-messages';
import WarningIcon from '../../assets/icons/Warning';

export const ValidationErrorTypes = {
  Frontend: 'Frontend',
  Backend: 'Backend',
} as const;
export type ValidationErrorType =
  (typeof ValidationErrorTypes)[keyof typeof ValidationErrorTypes];

export type errorsProps = {
  fieldName: string;
  fieldErrors: string[];
  fieldLabel: string;
  feature?: string;
  type: ValidationErrorType;
  affectedFields?: { fieldName: string; fieldLabel: string }[];
};

export type warningsProps = {
  fieldName: string;
  fieldWarnings: string[];
  fieldLabel: string;
  feature?: string;
  type: ValidationErrorType;
  affectedFields?: { fieldName: string; fieldLabel: string }[];
};

type Row = {
  msg: string;
  fields: { fieldName: string; fieldLabel: string; feature?: string }[];
  type: 'error' | 'warning';
};

interface Props {
  errors: errorsProps[];
  onIssueClick?: (
    fieldPath: Extract<keyof TypeOf<never>, string>,
    feature?: string,
    relatedClientName?: string,
    errorMessage?: string,
    type?: 'error' | 'warning'
  ) => void;
  warnings?: warningsProps[];
  sx?: SxProps;
}

const SummaryErrors: React.FC<Props> = ({
  errors,
  onIssueClick = () => {},
  warnings,
  sx,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  if ((!errors || errors.length === 0) && (!warnings || warnings.length === 0))
    return;

  const formatFieldLabel = (text: string) => {
    const spaced = String(text).replace(/([a-z0-9])([A-Z])/g, '$1 $2');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  };

  const backendErrors = errors.filter(
    (e) => e.type === ValidationErrorTypes.Backend
  );
  const frontendErrors = errors.filter(
    (e) => e.type === ValidationErrorTypes.Frontend
  );

  const frontendRows: Row[] = frontendErrors.flatMap((error) =>
    error.fieldErrors.map((msg) => ({
      msg,
      fields: [
        {
          fieldName: error.fieldName,
          fieldLabel: error.fieldLabel,
          feature: error.feature,
        },
      ],
      type: 'error',
    }))
  );

  const backendRows: Row[] = backendErrors.map((e) => ({
    msg: e.fieldErrors[0] ?? '',
    fields: e.affectedFields?.length
      ? e.affectedFields.map((f) => ({
          fieldName: f.fieldName,
          fieldLabel: f.fieldLabel,
          feature: e.feature,
        }))
      : [
          {
            fieldName: e.fieldName,
            fieldLabel: e.fieldLabel,
            feature: e.feature,
          },
        ],
    type: 'error',
  }));

  const warningsRows: Row[] =
    warnings?.flatMap((warning) =>
      warning.fieldWarnings.map((msg) => ({
        msg,
        fields: [
          {
            fieldName: warning.fieldName,
            fieldLabel: warning.fieldLabel,
            feature: warning.feature,
          },
        ],
        type: 'warning',
      }))
    ) ?? [];

  const allRows: Row[] = [...frontendRows, ...backendRows, ...warningsRows];
  const totalErrors = allRows.length;
  const summaryType: 'error' | 'warning' =
    frontendErrors.length > 0 || backendErrors.length > 0 ? 'error' : 'warning';

  return (
    <ClickAwayListener onClickAway={() => setIsOpen(false)}>
      <Box
        data-testid="summary-errors"
        sx={
          {
            ...summaryWrapStyles,
            ...sx,
          } as SxProps
        }
      >
        <Box
          sx={summaryTopStyles({ type: summaryType })}
          onClick={() => setIsOpen(!isOpen)}
        >
          {summaryType === 'error' ? <ErrorIcon /> : <WarningIcon />}
          <Typography>
            Action needed: please address the following error
            {totalErrors > 1 && 's'} ({totalErrors})
          </Typography>
        </Box>
        <Box
          component="ul"
          sx={{
            ...summaryErrorsStyles({
              type: summaryType,
            }),
            display: isOpen ? 'block' : 'none',
          }}
        >
          {allRows.map(({ msg, fields, type }, index) => (
            <Box
              component="li"
              key={index}
              sx={{
                ...summaryErrorStyles({ type: summaryType }),
                mb: 2,
              }}
            >
              <Box sx={summaryErrorIconStyles}>
                {type === 'error' ? <ErrorIcon /> : <WarningIcon />}
              </Box>
              <Box
                sx={{ display: 'flex', gap: '8px', flexDirection: 'column' }}
              >
                <Typography
                  component="span"
                  onClick={() => {
                    const relatedClientName = extractRelatedClientName(msg);
                    onIssueClick(
                      fields[0].fieldName,
                      fields[0].feature,
                      relatedClientName ?? undefined,
                      msg,
                      'error'
                    );
                    setIsOpen(false);
                  }}
                  sx={summaryErrorTextStyles({ type })}
                >
                  <span dangerouslySetInnerHTML={{ __html: msg }} />
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'baseline',
                    gap: '8px',
                    flexWrap: 'wrap',
                  }}
                >
                  {fields
                    .filter((f) => !!f.fieldLabel)
                    .map((f, idx) => (
                      <Chip
                        key={idx}
                        label={formatFieldLabel(f.fieldLabel)}
                        size="small"
                        onClick={() => {
                          const relatedClientName =
                            extractRelatedClientName(msg);
                          onIssueClick(
                            f.fieldName,
                            f.feature,
                            relatedClientName ?? undefined,
                            msg,
                            'warning'
                          );
                          setIsOpen(false);
                        }}
                        elevated
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </ClickAwayListener>
  );
};

export default SummaryErrors;
